import { FormControl } from '@/components/form-control'
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from '@/components/mobile-drawer'
import { Button } from '@/components/ui/deprecated-button'
import { Input } from '@/components/ui/input'
import {
  DEFAULT_FILTER_LOCATION,
  formatJobType,
} from '@/modules/salary/common/utils'
import { JobTypeSelect } from '@/modules/salary/filters/desktop/job-type-select'
import { LocationDrawer } from '@/modules/salary/filters/mobile/location-drawer'
import { SpecialtyDrawer } from '@/modules/salary/filters/mobile/specialty-drawer'
import { useSelectedSpecialty } from '@/modules/salary/filters/use-salary-filters'
import { Search, X } from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { cn } from '@/lib/css-utils'
import { ProfessionSelect } from '@/modules/salary/filters/desktop/profession-select'
import { SalaryFilterControls } from '@/modules/salary/types'
import { SalaryFilterParam } from '../../constants'
import { SalaryFilterControl } from '../../constants'
import { useSalaryHistogram } from '@/modules/salary/histogram/use-salary-histogram'

enum FilterSection {
  Specialty = 'Specialty',
  Location = 'Location',
  JobType = 'JobType',
}

function FiltersDrawer({
  isOpen,
  onClose,
  onApplyFilters,
  loading,
  isAuthenticated,
}: {
  isOpen: boolean
  onClose: () => void
  onApplyFilters: (fields: Partial<SalaryFilterControls>) => void
  loading: boolean
  isAuthenticated: boolean
}) {
  const [openSection, setOpenSection] = useState<FilterSection | null>(null)
  const { watch, handleSubmit, formState, setValue } =
    useFormContext<SalaryFilterControls>()
  const isDirty = formState.isDirty
  const values = watch()
  const selectedSpecialty = useSelectedSpecialty({
    specialtySlug: values[SalaryFilterControl.specialty],
  })

  const onOpenSection = (section: FilterSection) => {
    setOpenSection(section)
  }

  const onCloseSection = () => {
    setOpenSection(null)
  }

  const applyFilters = () => {
    // no changes just close drawer
    if (!isDirty) {
      onClose()
      return
    }

    handleSubmit((filters) => {
      onApplyFilters(filters)
    })()
  }

  return (
    <>
      <Drawer
        snapPoints={[1]}
        fadeFromIndex={0}
        onClose={onClose}
        open={isOpen}
        direction="right"
      >
        <DrawerContent>
          <DrawerHeader>Modify Search</DrawerHeader>
          <div
            className="p-4 flex flex-col gap-4 h-full bg-modal-bg"
            style={{ height: 'calc(100vh - 120px)' }}
          >
            <ProfessionSelect
              name={SalaryFilterControl.profession}
              selectClassName="py-2 border border-input-border rounded-lg px-4 hover:border-input-border-focus \
               dark:border-input-border dark:hover:border-input-border-focus focus:border \
               focus:border-input-border-focus"
            />
            <FormControl label="Specialty">
              <Input
                value={selectedSpecialty?.fullName ?? ''}
                placeholder="Search specialties"
                onClick={() => onOpenSection(FilterSection.Specialty)}
                readOnly
                RightIcon={({ className }) => {
                  if (!values[SalaryFilterControl.specialty]) return null
                  return (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setValue(SalaryFilterControl.specialty, '', {
                          shouldDirty: true,
                        })
                      }}
                      className={cn('p-0', className)}
                    >
                      <X className="w-4" />
                    </Button>
                  )
                }}
              />
            </FormControl>
            <FormControl label="Location">
              <Input
                value={
                  (values[SalaryFilterControl.state]?.label as string) ?? ''
                }
                placeholder={DEFAULT_FILTER_LOCATION}
                onClick={() => onOpenSection(FilterSection.Location)}
                readOnly
                RightIcon={({ className }) => {
                  if (!values[SalaryFilterControl.state]) return null
                  return (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setValue(SalaryFilterControl.state, null, {
                          shouldDirty: true,
                        })
                      }}
                      className={cn('p-0', className)}
                    >
                      <X className="w-4" />
                    </Button>
                  )
                }}
              />
            </FormControl>
            <JobTypeSelect
              name={SalaryFilterParam.jobType}
              className="w-full border border-b-input-border hover:border-b-input-border-focus rounded-md"
              label="Job Type"
            />
          </div>
          <DrawerFooter>
            <Button
              className="w-full"
              onClick={() => {
                applyFilters()
              }}
              size="lg"
              isLoading={loading}
            >
              Search
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <SpecialtyDrawer
        isOpen={openSection === FilterSection.Specialty}
        onClose={onCloseSection}
        selectedSpecialty={selectedSpecialty ?? null}
        professionSlug={values.profession}
        isAuthenticated={isAuthenticated}
      />
      <LocationDrawer
        isOpen={openSection === FilterSection.Location}
        onClose={onCloseSection}
        selectedLocation={values[SalaryFilterControl.state] ?? null}
      />
    </>
  )
}

function FirstLineMobileFilters() {
  const { data: histogramData } = useSalaryHistogram()
  const professionWithSpecialty = histogramData?.getHistogram?.histogramTitle

  return <p className="font-semibold truncate">{professionWithSpecialty}</p>
}

export function FiltersMobile({
  filtersValues,
  onApplyFilters,
  loading,
  isAuthenticated,
}: {
  filtersValues: Partial<SalaryFilterControls> | null
  onApplyFilters: (fields: Partial<SalaryFilterControls>) => void
  loading: boolean
  isAuthenticated: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [wasLoading, setWasLoading] = useState(false)
  const locationLabel = filtersValues?.[SalaryFilterControl.state]?.label

  useEffect(() => {
    if (wasLoading && !loading) {
      setIsOpen(false)
    }
    setWasLoading(loading)
  }, [loading, wasLoading])

  const secondaryFilters = [
    locationLabel || DEFAULT_FILTER_LOCATION,
    formatJobType(filtersValues?.jobType),
  ].filter(Boolean)

  return (
    <>
      <Button
        variant="ghost"
        className="w-full flex bg-background-primary p-3 gap-4 text-sm items-center shadow-sm border-0 rounded-xl
      justify-start text-start dark:border dark:border-border-muted"
        onClick={() => setIsOpen(true)}
      >
        <Search className="stroke-copy-muted w-6 h-6" />
        <div className="overflow-hidden">
          <FirstLineMobileFilters />

          <p className="text-copy-muted flex gap-1.5">
            {secondaryFilters.map((secondaryFilter, i) => {
              return (
                <Fragment key={i}>
                  {i > 0 ? <span className="font-semibold">·</span> : ''}
                  <span>{secondaryFilter}</span>
                </Fragment>
              )
            })}
          </p>
        </div>
      </Button>
      <FiltersDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onApplyFilters={onApplyFilters}
        loading={loading}
        isAuthenticated={isAuthenticated}
      />
    </>
  )
}
